/**
 * Carbon Credit Marketplace — /marketplace
 * Browse and purchase verified DPCS+ carbon credits.
 */
import React, { useState, useEffect, useCallback } from "react";
import client from "@/api/client";
import { toast } from "sonner";
import { Leaf, ShoppingCart, RefreshCw, Check, X, Shield, Info, TrendingUp, Users, Star } from "lucide-react";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const VERIF_CONFIG = {
  third_party_verified: { color:"bg-emerald-50 text-emerald-700", label:"3P Verified" },
  analyst_reviewed:     { color:"bg-indigo-50 text-indigo-700",   label:"Analyst Rev." },
  system_validated:     { color:"bg-blue-50 text-blue-700",       label:"System Val." },
  self_reported:        { color:"bg-slate-100 text-slate-500",    label:"Self-Reported" },
};
const GRADE_COLORS = { excellent:"text-emerald-700 bg-emerald-50", good:"text-blue-700 bg-blue-50", moderate:"text-amber-700 bg-amber-50", low:"text-slate-500 bg-slate-100" };

function ListingCard({ listing, onBuy }) {
  const verifCfg = VERIF_CONFIG[listing.credit?.verification_level] || VERIF_CONFIG.self_reported;
  const [pricing,   setPricing]   = useState(null);
  const [matches,   setMatches]   = useState([]);
  const [showExtra, setShowExtra] = useState(false);
  const [showInterest, setShowInterest] = useState(false);
  const [interestForm, setIntForm] = useState({ buyer_name:"", buyer_email:"", buyer_type:"corporate", desired_volume:"", deal_type:"spot", notes:"" });
  const [submittingInterest, setSubInt] = useState(false);

  useEffect(() => {
    if (showExtra && listing.credit_id && !pricing) {
      Promise.allSettled([
        client.get(`/credits/pricing/${listing.credit_id}`),
        client.get(`/marketplace/match/${listing.credit_id}`),
      ]).then(([pRes, mRes]) => {
        if (pRes.status === "fulfilled") setPricing(pRes.value.data);
        if (mRes.status === "fulfilled") setMatches(mRes.value.data?.top_segments?.slice(0,3) || []);
      });
    }
  }, [showExtra, listing.credit_id, pricing]);

  const handleInterest = async (e) => {
    e.preventDefault();
    setSubInt(true);
    try {
      await client.post("/marketplace/interest", {
        listing_id: listing.listing_id, credit_id: listing.credit_id,
        ...interestForm, desired_volume: interestForm.desired_volume ? Number(interestForm.desired_volume) : undefined,
      });
      toast.success("Interest registered! Our team will contact you within 24 hours.");
      setShowInterest(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSubInt(false);
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-sm transition-shadow"
      data-testid={`listing-${listing.listing_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
              <Leaf size={14} className="text-[#059669]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">{listing.methodology?.name || "DPCS+ Credits"}</p>
              <p className="text-xs text-slate-400">{listing.seller_name}</p>
            </div>
          </div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${verifCfg.color}`}>
          {verifCfg.label}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          ["Volume",  `${(listing.volume_available || 0).toFixed(1)} t`, "text-slate-900"],
          ["Price",   `$${listing.price_per_tonne}/t`,                   "text-[#059669]"],
          ["Vintage", `${listing.credit?.vintage_year || "—"}`,          "text-slate-600"],
        ].map(([label, value, color]) => (
          <div key={label} className="bg-slate-50 rounded-xl p-2.5 text-center">
            <p className={`text-sm font-bold ${color} tabular-nums`}>{value}</p>
            <p className="text-xs text-slate-400">{label}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between text-xs text-slate-400 mb-3">
        <span>Serial: <span className="font-mono text-slate-600">{listing.credit?.serial_range || "—"}</span></span>
        <span>Total: <strong className="text-slate-900">${(listing.total_value_usd || 0).toFixed(0)}</strong></span>
      </div>

      {listing.description && (
        <p className="text-xs text-slate-400 italic mb-3 line-clamp-2">"{listing.description}"</p>
      )}

      <button onClick={() => onBuy(listing)} data-testid={`buy-${listing.listing_id}`}
        className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#059669] text-white text-sm font-semibold rounded-xl hover:bg-emerald-700">
        <ShoppingCart size={13} /> Buy & Retire Credits
      </button>

      {/* Pricing + Matching expandable */}
      <div className="mt-2">
        <button onClick={() => setShowExtra(!showExtra)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 w-full justify-center py-1">
          <TrendingUp size={11} /> {showExtra ? "Hide" : "Show"} pricing & buyer insights
        </button>
        {showExtra && (
          <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
            {pricing && (
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs font-semibold text-slate-500 mb-2">Pricing Engine</p>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Recommended</span>
                  <span className="font-bold text-slate-900">${pricing.recommended_price}/t</span>
                </div>
                <div className="flex justify-between text-xs text-slate-400 mt-0.5">
                  <span>Range: {pricing.price_range?.label}</span>
                  <span className="font-medium">{pricing.confidence} confidence</span>
                </div>
                {pricing.drivers?.slice(0,2).map((d,i) => (
                  <div key={i} className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>{d.factor}</span><span className="font-semibold">{d.impact}</span>
                  </div>
                ))}
              </div>
            )}
            {matches.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
                  <Users size={11} /> Top Buyer Segments
                </p>
                {matches.map((m,i) => (
                  <div key={i} className="flex items-center justify-between py-1">
                    <span className="text-[11px] text-slate-600 truncate flex-1">{m.segment_name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ml-2 ${GRADE_COLORS[m.grade]}`}>{m.match_score}</span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => setShowInterest(!showInterest)} data-testid={`interest-${listing.listing_id}`}
              className="w-full text-xs text-indigo-600 font-medium border border-indigo-200 rounded-lg py-1.5 hover:bg-indigo-50">
              Express Deal Interest
            </button>
            {showInterest && (
              <form onSubmit={handleInterest} className="space-y-2 bg-indigo-50 rounded-xl p-3">
                {[["buyer_name","Name *","text"],["buyer_email","Email *","email"]].map(([k,l,t])=>(
                  <input key={k} type={t} required value={interestForm[k]} onChange={e=>setIntForm(f=>({...f,[k]:e.target.value}))} placeholder={l}
                    className="w-full h-8 px-2 text-xs border border-slate-200 rounded-lg focus:outline-none" />
                ))}
                <div className="grid grid-cols-2 gap-2">
                  <select value={interestForm.deal_type} onChange={e=>setIntForm(f=>({...f,deal_type:e.target.value}))}
                    className="h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white">
                    {["spot","forward","offtake"].map(d=><option key={d} value={d}>{d}</option>)}
                  </select>
                  <input type="number" placeholder="Volume (t)" value={interestForm.desired_volume} onChange={e=>setIntForm(f=>({...f,desired_volume:e.target.value}))}
                    className="h-8 px-2 text-xs border border-slate-200 rounded-lg" />
                </div>
                <button type="submit" disabled={submittingInterest}
                  className="w-full h-8 bg-indigo-600 text-white text-xs font-medium rounded-lg disabled:opacity-50">
                  {submittingInterest ? "Sending..." : "Submit Interest"}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PurchaseModal({ listing, onClose, onSuccess }) {
  const [form, setForm] = useState({ buyer_name:"", buyer_email:"", quantity:"" });
  const [submitting, setSubmitting] = useState(false);
  const maxQty = listing.volume_available || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.buyer_name || !form.buyer_email) { toast.error("Name and email required"); return; }
    const qty = Number(form.quantity) || maxQty;
    if (qty <= 0 || qty > maxQty) { toast.error(`Quantity must be between 0.1 and ${maxQty.toFixed(2)}`); return; }
    setSubmitting(true);
    try {
      const res = await client.post("/marketplace/purchase", {
        listing_id: listing.listing_id,
        buyer_name: form.buyer_name,
        buyer_email: form.buyer_email,
        quantity: qty,
        retire_immediately: true,
      });
      toast.success(`Purchase complete! Receipt: ${res.data.receipt_id}`);
      onSuccess(res.data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Purchase failed"); }
    setSubmitting(false);
  };

  const qty = Number(form.quantity) || maxQty;
  const totalCost = (qty * listing.price_per_tonne).toFixed(2);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="purchase-modal">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-base font-bold text-slate-900">Buy & Retire Credits</h3>
            <p className="text-xs text-slate-400 mt-0.5">{listing.methodology?.name || "DPCS+"} · {listing.seller_name}</p>
          </div>
          <button onClick={onClose}><X size={16} className="text-slate-400" /></button>
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-5">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Available volume</span>
            <span className="font-bold text-slate-900">{maxQty.toFixed(2)} tCO₂e</span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-slate-600">Price</span>
            <span className="font-bold text-[#059669]">${listing.price_per_tonne}/tCO₂e</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {[["buyer_name","Your Name or Organization *","text"],["buyer_email","Email Address *","email"]].map(([k,l,t])=>(
            <div key={k}>
              <label className="text-xs font-medium text-slate-600 mb-1 block">{l}</label>
              <input type={t} value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} required
                className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-[#059669]" />
            </div>
          ))}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Quantity (tCO₂e) — leave blank for full volume</label>
            <input type="number" value={form.quantity} onChange={e=>setForm(f=>({...f,quantity:e.target.value}))}
              placeholder={`Max ${maxQty.toFixed(2)}`} min="0.1" max={maxQty} step="0.1"
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-[#059669]" />
          </div>

          <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Total cost (indicative)</span>
              <span className="font-bold text-slate-900">${totalCost}</span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Credits will be permanently retired as carbon offsets on purchase.
            </p>
          </div>

          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <Info size={12} className="mt-0.5 flex-shrink-0" />
            <p>Payment handled offline. This records your offset intention. DeCarbonIQ will contact you.</p>
          </div>

          <button type="submit" disabled={submitting}
            className="w-full h-11 bg-[#059669] text-white text-sm font-bold rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            {submitting ? "Processing..." : `Confirm: ${qty.toFixed(2)} tCO₂e for $${totalCost}`}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Marketplace() {
  const [listings, setListings] = useState([]);
  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [buying,   setBuying]   = useState(null);
  const [receipt,  setReceipt]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lRes, sRes] = await Promise.allSettled([
        client.get("/marketplace/listings"),
        client.get("/marketplace/stats"),
      ]);
      if (lRes.status === "fulfilled") setListings(lRes.value.data?.listings || []);
      if (sRes.status === "fulfilled") setStats(sRes.value.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 lg:p-8 min-h-screen bg-[#F8FAFC]" data-testid="marketplace-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
              <Leaf size={18} className="text-[#059669]" />
            </div>
            
      <PageHeader
        title="Carbon Credit Marketplace"
        subtitle="DPCS+ verified credits — traceable, serialised, registry-backed."
      />
          <p className="text-sm text-slate-500">DPCS+ verified credits — traceable, serialised, registry-backed.</p>
        </div>

        {/* Stats strip */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[
              ["Total Issued",  `${(stats.total_issued || 0).toFixed(1)} t`,  false],
              ["Available",     `${(stats.total_listed || 0).toFixed(1)} t`,  true ],
              ["Retired",       `${(stats.total_retired || 0).toFixed(1)} t`, false],
              ["Listings",      stats.total_credits || 0,                      false],
            ].map(([label, value, accent]) => (
              <MetricCard key={label} label={label} value={value} color={accent ? "text-emerald-700" : "text-slate-900"} />
            ))}
          </div>
        )}

        {/* Listings */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-56 bg-white border border-slate-200 rounded-xl animate-pulse" />)}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <Leaf size={40} className="mx-auto text-slate-200 mb-3" />
            <h3 className="text-sm font-semibold text-slate-900 mb-1">No active listings</h3>
            <p className="text-xs text-slate-400 max-w-sm mx-auto">
              Generate credits from your verified assessment, then list them here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {listings.map(lst => (
              <ListingCard key={lst.listing_id} listing={lst} onBuy={l => setBuying(l)} />
            ))}
          </div>
        )}

        {/* Purchase success */}
        {receipt && (
          <div className="fixed bottom-6 right-6 bg-white border border-emerald-200 rounded-xl p-5 shadow-xl max-w-sm z-50">
            <div className="flex items-start gap-3">
              <Check size={20} className="text-[#059669] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-900">Purchase recorded!</p>
                <p className="text-xs text-slate-500 mt-0.5">Receipt: <span className="font-mono">{receipt.receipt_id}</span></p>
                <p className="text-xs text-slate-500">{receipt.quantity_tco2e?.toFixed(2)} tCO₂e retired as carbon offset.</p>
              </div>
              <button onClick={() => setReceipt(null)} className="text-slate-300 hover:text-slate-500 ml-auto"><X size={14}/></button>
            </div>
          </div>
        )}

        {buying && (
          <PurchaseModal listing={buying} onClose={() => setBuying(null)} onSuccess={r => { setReceipt(r); load(); }} />
        )}
      </div>
  );
}
